import { Alert } from 'src/design/atoms/Alert';
import { Badge } from 'src/design/atoms/Badge';
import { Grid } from 'src/design/atoms/Grid';
import { Paper } from 'src/design/atoms/Paper';
import { Text } from 'src/design/atoms/Text';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { WorkflowDto, WorkflowStepDto } from 'src/api/FiretrolApiClient';
import { useWorkflowOptionsByDivision } from 'src/api/queries/workflows/useWorkflowOptionsByDivision';
import { useUpdateJobWorkflows } from 'src/api/queries/workflows/useJobWorkflowMutations';
import { WorkflowSelector } from 'src/app/selectors/WorkflowSelector';
import { DescriptionText } from 'src/design/atoms/DescriptionText';
import { useJobs } from 'src/hooks/useJobs';
import { SaveButton } from 'src/design/atoms/SaveButton';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { Header } from 'src/design/organisms/Header';
import { formatDate } from 'src/utils';

const hasApprovers = (step: WorkflowStepDto) =>
  step.approvalRoles.length > 0 || step.approvalUsers.length > 0;

const getWorkflowColumns = (
  pathName: string,
  jobId?: number,
): MRT_ColumnDef<WorkflowDto>[] => {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 200,
      Cell: ({ row }) => (
        <Link to={`${pathName}/${row.original.id}`}>{row.original.name}</Link>
      ),
    },
    {
      id: 'module',
      header: 'Module',
      accessorFn: (row) => row.resourceId?.toUpperCase(),
    },
    {
      id: 'lastModifiedBy',
      header: 'Last Modified By',
      size: 200,
      accessorFn: (row) =>
        `${row.lastUpdatedBy ? row.lastUpdatedBy.fullName : ''} (${formatDate(
          row.updatedAt,
        )})`,
    },
    {
      id: 'createdBy',
      header: 'Created By',
      accessorFn: (row) =>
        `${row.createdBy ? row.createdBy.fullName : ''} (${formatDate(
          row.createdAt,
        )})`,
    },
    {
      id: 'approvals',
      header: 'Approvals',
      accessorFn: (row) => (row.steps?.some(hasApprovers) ? 'Yes' : 'No'),
      Cell: ({ row }) => {
        const hasApproval = row.original.steps?.some(hasApprovers) ?? false;
        return (
          <Badge color={hasApproval ? 'green' : 'gray'}>
            {hasApproval ? 'Yes' : 'No'}
          </Badge>
        );
      },
    },
    {
      id: 'template',
      header: 'Template',
      accessorFn: (row) => (row.jobId === jobId ? 'No' : 'Yes'),
      Cell: ({ row }) => {
        const isTemplate = row.original.jobId !== jobId;
        return (
          <Badge color={isTemplate ? 'blue' : 'gray'}>
            {isTemplate ? 'Yes' : 'No'}
          </Badge>
        );
      },
    },
    {
      id: 'office',
      header: 'Office',
      accessorFn: (row) => (row.office ? row.office.name : 'Global'),
    },
    {
      id: 'finish',
      header: 'Finish',
      accessorFn: (row) => {
        const last = row.steps?.find((step) => step.isLastStep);
        return last ? last.label : '';
      },
    },
  ];
};

const WORKFLOW_FIELDS = [
  'rfiWorkflow',
  'submittalWorkflow',
  'drawingWorkflow',
  'transmittalWorkflow',
  'changeOrderWorkflow',
] as const;

type WorkflowField = typeof WORKFLOW_FIELDS[number];
type WorkflowEdits = Partial<Record<WorkflowField, WorkflowDto>>;

export const Workflows: React.FC = () => {
  const location = useLocation();
  const { selectedJob } = useJobs();
  // The job arrives after first render, so the selectors are derived from it
  // and only the fields the user has touched are held locally. A successful
  // save clears the edits, re-syncing every selector from the refreshed job.
  const [edits, setEdits] = React.useState<WorkflowEdits>({});

  const selectedWorkflow = (field: WorkflowField) =>
    edits[field] ?? selectedJob?.[field] ?? null;

  const isEdited = (field: WorkflowField) => {
    const edited = edits[field];
    return !!edited && edited.id !== selectedJob?.[`${field}Id`];
  };

  const editWorkflow = (field: WorkflowField) => (value: WorkflowDto) =>
    setEdits((prev) => ({ ...prev, [field]: value }));

  const { data, isLoading, isFetching, isError } = useWorkflowOptionsByDivision(
    selectedJob?.divisionId,
    'all',
  );
  const workflows = React.useMemo(() => data ?? [], [data]);
  const updateWorkflows = useUpdateJobWorkflows();

  const saveWorkflowSettings = async () => {
    if (
      !selectedJob ||
      WORKFLOW_FIELDS.every((field) => !selectedWorkflow(field))
    )
      return;
    await updateWorkflows.mutateAsync({
      jobId: selectedJob.id,
      divisionId: selectedJob.divisionId ?? undefined,
      settings: {
        rfiWorkflowId: selectedWorkflow('rfiWorkflow')?.id ?? undefined,
        submittalWorkflowId:
          selectedWorkflow('submittalWorkflow')?.id ?? undefined,
        drawingWorkflowId: selectedWorkflow('drawingWorkflow')?.id ?? undefined,
        transmittalWorkflowId:
          selectedWorkflow('transmittalWorkflow')?.id ?? undefined,
        changeOrderWorkflowId:
          selectedWorkflow('changeOrderWorkflow')?.id ?? undefined,
      },
    });
    setEdits({});
  };

  const columns = useMemo(
    () => getWorkflowColumns(location.pathname, selectedJob?.id),
    [location.pathname, selectedJob?.id],
  );

  const tableState = useTableState({
    identifier: 'job-workflows',
    columns: columns,
    defaultSorting: [{ id: 'name', desc: false }],
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: false,
      enableClickToCopy: false,
      columns: columns,
      data: workflows,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.id),
    totalCount: workflows.length,
    isLoading,
    isFetching,
    isError,
  });

  if (!selectedJob) return null;

  const isServiceJob = selectedJob.jobType === 'service';

  return (
    <>
      <Header
        title="Workflows"
        breadcrumbProps={{
          items: [
            { text: 'Jobs', href: '/jobs' },
            {
              text: `${selectedJob?.name} - ${selectedJob?.jobNumber}`,
              href: `/jobs/${selectedJob?.id}/summary`,
            },
            { text: 'Workflows' },
          ],
        }}
        helpText="Configure workflow settings for this job and view its available workflows."
      />
      <Grid className="m-b-20">
        <Grid.Col span={12}>
          <Paper p="md" withBorder>
            <h3 className="m-t-0">Workflow Settings</h3>
            {isServiceJob && (
              <Alert mb="md" color="blue">
                Workflow settings for service jobs are managed at the office
                level. Individual service jobs cannot have custom workflow
                configurations.
              </Alert>
            )}
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Text mb="xs" c="black" fw={500}>
                  RFI Workflow
                </Text>
                <WorkflowSelector
                  options={workflows}
                  resourceId="rfi"
                  value={selectedWorkflow('rfiWorkflow')}
                  onChange={editWorkflow('rfiWorkflow')}
                  disabled={isServiceJob}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Text mb="xs" c="black" fw={500}>
                  Submittal Workflow
                </Text>
                <WorkflowSelector
                  options={workflows}
                  resourceId="submittal"
                  value={selectedWorkflow('submittalWorkflow')}
                  onChange={editWorkflow('submittalWorkflow')}
                  disabled={isServiceJob}
                />
              </Grid.Col>
            </Grid>
            <Grid className="m-t-15">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Text mb="xs" c="black" fw={500}>
                  Drawing Workflow
                </Text>
                <WorkflowSelector
                  options={workflows}
                  resourceId="drawing"
                  value={selectedWorkflow('drawingWorkflow')}
                  onChange={editWorkflow('drawingWorkflow')}
                  disabled={isServiceJob}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Text mb="xs" c="black" fw={500}>
                  Transmittal Workflow
                </Text>
                <WorkflowSelector
                  options={workflows}
                  resourceId="transmittal"
                  value={selectedWorkflow('transmittalWorkflow')}
                  onChange={editWorkflow('transmittalWorkflow')}
                  disabled={isServiceJob}
                />
              </Grid.Col>
            </Grid>
            <Grid className="m-t-15">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Text mb="xs" c="black" fw={500}>
                  Timesheet Workflow
                </Text>
                <WorkflowSelector
                  options={workflows}
                  resourceId="timesheet"
                  value={selectedJob.timesheetWorkflow ?? null}
                  onChange={() => {}}
                  disabled
                />
                <DescriptionText>
                  Timesheet Workflow is managed per Office.
                </DescriptionText>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Text mb="xs" c="black" fw={500}>
                  Change Order Workflow
                </Text>
                <WorkflowSelector
                  options={workflows}
                  resourceId="change"
                  value={selectedWorkflow('changeOrderWorkflow')}
                  onChange={editWorkflow('changeOrderWorkflow')}
                  disabled={isServiceJob}
                />
              </Grid.Col>
            </Grid>
            <Grid className="m-t-20 text-right">
              <Grid.Col>
                <SaveButton
                  disabled={isServiceJob || !WORKFLOW_FIELDS.some(isEdited)}
                  loading={updateWorkflows.isPending}
                  onClick={saveWorkflowSettings}
                >
                  Save Settings
                </SaveButton>
              </Grid.Col>
            </Grid>
          </Paper>
        </Grid.Col>
      </Grid>

      {table}
    </>
  );
};
